import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import styles from './UploadPage.module.css';
import API_BASE_URL from '../config/api';
import { Helmet } from 'react-helmet-async';
import { compressFilesSequentially } from '../utils/imageCompression';
import { useTranslation } from 'react-i18next';
import { auth, storage } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const DIFFICULTIES = [
  'F', 'PD-', 'PD', 'PD+', 'AD-', 'AD', 'AD+',
  'D-', 'D', 'D+', 'TD-', 'TD', 'TD+',
  'ED-', 'ED', 'ED+', 'ABO'
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MOUNTAIN_RANGES = [
  'Alps', 'Himalaya', 'Andes', 'Rockies', 'Caucasus',
  'Carpathians', 'Hindu Kush', 'Pyrenees', 'Atlas',
  'Japanese Alps', 'Southern Alps'
];

const MIN_MOUNTAIN_HEIGHT = 600;
const MAX_MOUNTAIN_HEIGHT = 8848;

function UploadPage() {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const fileInputRef = useRef(null);
  const imageSelectionTokenRef = useRef(0);
  const { t } = useTranslation();

  const [countries, setCountries] = useState([]);
  const [showCountryOther, setShowCountryOther] = useState(false);
  const [showRangeOther, setShowRangeOther] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    height: '',
    country: '',
    countryOther: '',
    mountainRange: '',
    mountainRangeOther: '',
    difficulty: '',
    gearProfile: '',
    seasonStart: '',
    seasonEnd: '',
    images: [],
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // AI auto-fill removed per project cleanup request.

  // Fetch countries (REST Countries API as a reliable public source)
  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      try {
        const res = await fetch('https://restcountries.com/v3.1/all?fields=name');
        if (!res.ok) throw new Error('Failed to load countries');
        const data = await res.json();
        const names = data
          .map(c => c?.name?.common)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        if (isMounted) setCountries(names);
      } catch (_) {
        if (isMounted) {
          // Fallback minimal list
          setCountries([
            'Austria', 'Germany', 'Italy', 'Switzerland', 'France', 'Slovenia', 'Pakistan', 'India', 'China', 'Peru',
            'United States', 'United Kingdom', 'Spain', 'Nepal', 'Argentina', 'Canada', 'Japan', 'Morocco',
          ]);
        }
      }
    };
    fetchCountries();
    return () => { isMounted = false; };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    if (value === '__OTHER__') {
      setShowCountryOther(true);
      setFormData(prev => ({ ...prev, country: '' }));
    } else {
      setShowCountryOther(false);
      setFormData(prev => ({ ...prev, country: value, countryOther: '' }));
    }
  };

  const handleRangeChange = (e) => {
    const value = e.target.value;
    if (value === '__OTHER__') {
      setShowRangeOther(true);
      setFormData(prev => ({ ...prev, mountainRange: '' }));
    } else {
      setShowRangeOther(false);
      setFormData(prev => ({ ...prev, mountainRange: value, mountainRangeOther: '' }));
    }
  };

  const handleImageChange = async (e) => {
    const selectionToken = ++imageSelectionTokenRef.current;
    const selectedFiles = Array.from(e.target.files || []);
    let currentErrors = [];
    let processedFiles = [];

    selectedFiles.forEach(file => {
      if (!file.type.startsWith('image/')) {
        currentErrors.push(t('uploadMountain.errors.fileType', { name: file.name }));
      } else if (file.size > 10 * 1024 * 1024) {
        currentErrors.push(t('uploadMountain.errors.fileSize', { name: file.name }));
      } else {
        processedFiles.push(file);
      }
    });

    if (currentErrors.length > 0) {
      if (selectionToken !== imageSelectionTokenRef.current) return;
      setError(currentErrors.join(' '));
      setFormData(prev => ({ ...prev, images: [] }));
    } else {
      try {
        const compressed = await compressFilesSequentially(processedFiles);
        if (selectionToken !== imageSelectionTokenRef.current) return;
        setFormData(prev => ({ ...prev, images: compressed }));
        setError('');
      } catch (compressionError) {
        console.error('Failed to compress mountain images', compressionError);
        if (selectionToken !== imageSelectionTokenRef.current) return;
        setFormData(prev => ({ ...prev, images: processedFiles }));
        setError(t('uploadMountain.errors.compression'));
      }
    }
  };

  const handleAiGenerate = async () => {
    const name = formData.name.trim();
    if (!name) {
      alert('Please enter a mountain name first.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/mountains/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser?.token}`
        },
        body: JSON.stringify({ mountainName: name })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'AI generation failed');
      }

      const data = result.data;
      
      // Update visibility controls for "Other" fields before setting data
      const isCountryOther = data.country && !countries.includes(data.country);
      const isRangeOther = data.mountainRange && !MOUNTAIN_RANGES.includes(data.mountainRange);
      
      setShowCountryOther(!!isCountryOther);
      setShowRangeOther(!!isRangeOther);

      setFormData(prev => ({
        ...prev,
        height: data.height ? String(data.height) : prev.height,
        country: !isCountryOther ? data.country : '',
        countryOther: isCountryOther ? data.country : '',
        mountainRange: !isRangeOther ? data.mountainRange : '',
        mountainRangeOther: isRangeOther ? (data.mountainRange === 'Other' ? '' : data.mountainRange) : '',
        difficulty: data.difficulty || prev.difficulty,
        gearProfile: data.gearProfile || prev.gearProfile,
        seasonStart: data.seasonStart || prev.seasonStart,
        seasonEnd: data.seasonEnd || prev.seasonEnd,
        description: data.description || prev.description
      }));

    } catch (err) {
      console.error(err);
      alert('Failed to auto-fill data. Please try again or fill manually.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFormData({
      name: '',
      height: '',
      country: '',
      countryOther: '',
      mountainRange: '',
      mountainRangeOther: '',
      difficulty: '',
      gearProfile: '',
      seasonStart: '',
      seasonEnd: '',
      images: [],
      description: ''
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError(t('uploadMountain.errors.name'));
      return;
    }
    const heightValue = Number(formData.height);
    if (!heightValue || Number.isNaN(heightValue)) {
      setError(t('uploadMountain.errors.height'));
      return;
    }
    if (heightValue < MIN_MOUNTAIN_HEIGHT || heightValue > MAX_MOUNTAIN_HEIGHT) {
      setError(t('uploadMountain.errors.heightRange', { min: MIN_MOUNTAIN_HEIGHT, max: MAX_MOUNTAIN_HEIGHT }));
      return;
    }
    const countryValue = showCountryOther ? formData.countryOther.trim() : formData.country.trim();
    const rangeValue = showRangeOther ? formData.mountainRangeOther.trim() : formData.mountainRange.trim();
    if (!countryValue) {
      setError(t('uploadMountain.errors.country'));
      return;
    }
    if (formData.images.length === 0) {
      setError(t('uploadMountain.errors.images'));
      return;
    }
    if (!currentUser) {
      setError(t('uploadMountain.errors.login'));
      return;
    }

    setLoading(true);
    let uploadedObjects = [];
    let cleanupDone = false;

    const cleanupUploadedObjects = async () => {
      if (cleanupDone || uploadedObjects.length === 0) return;
      cleanupDone = true;
      await Promise.all(
        uploadedObjects.map(({ objectPath }) =>
          deleteObject(storageRef(storage, objectPath)).catch(() => null)
        )
      );
    };

    try {
      const reserveFolderResponse = await fetch(`${API_BASE_URL}/api/mountains/reserve-storage-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      if (!reserveFolderResponse.ok) {
        const reserveFolderErrorText = await reserveFolderResponse.text();
        throw new Error(`Failed to reserve storage folder: ${reserveFolderResponse.status} ${reserveFolderResponse.statusText}. ${reserveFolderErrorText}`);
      }
      const reserveFolderResult = await reserveFolderResponse.json();
      const reservedStorageFolderId = Number(reserveFolderResult?.data?.id);
      if (!reserveFolderResult?.success || !Number.isInteger(reservedStorageFolderId) || reservedStorageFolderId <= 0) {
        throw new Error('Failed to reserve a valid storage folder ID.');
      }

      // 1) Upload images directly to Firebase Storage under a user-owned prefix.
      // This avoids multipart parsing issues ("Unexpected end of form") on the API.
      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error('Not authenticated');
      }

      const uploadResults = await Promise.allSettled(
        formData.images.map(async (file, index) => {
          const originalExt = (file.name && file.name.includes('.')) ? file.name.split('.').pop().toLowerCase() : 'jpg';
          const ext = /^[a-z0-9]+$/.test(originalExt) ? originalExt : 'jpg';
          const objectPath = `mountains/${reservedStorageFolderId}/${index + 1}.${ext}`;
          const objectRef = storageRef(storage, objectPath);
          await uploadBytes(objectRef, file, { contentType: file.type || 'image/jpeg' });
          const downloadUrl = await getDownloadURL(objectRef);
          return { objectPath, downloadUrl };
        })
      );

      const successfulUploads = uploadResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      uploadedObjects = successfulUploads;

      const failedUploads = uploadResults.filter(result => result.status === 'rejected');
      if (failedUploads.length > 0) {
        await cleanupUploadedObjects();
        throw new Error('One or more images failed to upload. Please try again.');
      }

      const uploadedImageUrls = successfulUploads.map(item => item.downloadUrl);

      // 2) Create mountain doc via JSON, referencing the uploaded URLs.
      const payload = {
        name: formData.name,
        height: String(heightValue),
        country: countryValue,
        mountainRange: rangeValue || '',
        needsEquipment: formData.needsEquipment,
        description: formData.description,
        difficulty: formData.difficulty || '',
        gearProfile: formData.gearProfile || '',
        seasonStart: formData.seasonStart || '',
        seasonEnd: formData.seasonEnd || '',
        reservedStorageFolderId,
        imageUrls: uploadedImageUrls,
      };

      const response = await fetch(`${API_BASE_URL}/api/mountains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        await cleanupUploadedObjects();
        throw new Error(`Upload failed: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        resetForm();
        navigate('/');
        window.location.reload();
      } else {
        await cleanupUploadedObjects();
        setError(result.message || t('uploadMountain.errors.submit'));
      }
    } catch (err) {
      await cleanupUploadedObjects();
      console.error('Upload error:', err);
      setError(`${t('uploadMountain.errors.network')} ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------ RETURN ---------------------------------------------------
  return (
    <>
      <Helmet>
        <title>{t('uploadMountain.helmetTitle')}</title>
        <meta
          name="description"
          content={t('uploadMountain.helmetDescription')}
        />
      </Helmet>
      <div className={styles.uploadPageContainer}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <Link to="/browse" className="backButton">
          &larr; {t('action.back')}
        </Link>
        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>{t('uploadMountain.title')}</h1>
      </div>

      {!currentUser ? (
        <div>
          <div className={styles.error} style={{ marginBottom: '1rem' }}>
            {t('uploadMountain.errors.login')}
          </div>
          <p>
            {t('uploadMountain.goBack')}{' '}
            <Link to="/browse" className="backButton">
              &larr; {t('uploadMountain.homeLink')}
            </Link>
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.formGroup}>
            <label htmlFor="name">{t('uploadMountain.fields.name')}</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={styles.input}
                placeholder={t('uploadMountain.fields.namePlaceholder')}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className={styles.aiButton}
                onClick={handleAiGenerate}
                disabled={loading}
                title="Auto-fill data with AI"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L14.39 8.26L20 12L14.39 15.74L12 22L9.61 15.74L4 12L9.61 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('common.ai', 'Upload with AI')}
              </button>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="height">{t('uploadMountain.fields.height')}</label>
              <input
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                required
                className={styles.input}
                placeholder={t('uploadMountain.fields.heightPlaceholder')}
                min={MIN_MOUNTAIN_HEIGHT}
                max={MAX_MOUNTAIN_HEIGHT}
                step="1"
                style={{ width: '100%' }}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="country">{t('uploadMountain.fields.country')}</label>
              <select
                id="country"
                name="country"
                value={showCountryOther ? '__OTHER__' : formData.country}
                onChange={handleCountryChange}
                required
                className={styles.input}
              >
                <option value="">{t('uploadMountain.selects.country')}</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="__OTHER__">{t('uploadMountain.selects.other')}</option>
              </select>
              {showCountryOther && (
                <input
                  type="text"
                  id="countryOther"
                  name="countryOther"
                  value={formData.countryOther}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder={t('uploadMountain.fields.countryPlaceholder')}
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="mountainRange">{t('uploadMountain.fields.mountainRange')}</label>
            <select
              id="mountainRange"
              name="mountainRange"
              value={showRangeOther ? '__OTHER__' : formData.mountainRange}
              onChange={handleRangeChange}
              className={styles.input}
            >
              <option value="">{t('uploadMountain.selects.range')}</option>
              {MOUNTAIN_RANGES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
              <option value="__OTHER__">{t('uploadMountain.selects.other')}</option>
            </select>
            {showRangeOther && (
              <input
                type="text"
                id="mountainRangeOther"
                name="mountainRangeOther"
                value={formData.mountainRangeOther}
                onChange={handleChange}
                className={styles.input}
                placeholder={t('uploadMountain.fields.mountainRangePlaceholder')}
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="difficulty">
              {t('uploadMountain.fields.difficulty')}
              <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', fontSize: '0.9rem', opacity: 0.8 }}>
                {t('uploadMountain.fields.difficultyCheck')}
                <a
                  href="https://www.bergfreunde.eu/alpine-grades-calculator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.difficultyInfoLink}
                  title={t('mountainPage.details.difficultyInfo')}
                >
                  ?
                </a>
              </span>
            </label>
            <select
              id="difficulty"
              name="difficulty"
              value={formData.difficulty}
              onChange={handleChange}
              className={styles.input}
            >
              <option value="">{t('uploadMountain.selects.difficulty')}</option>
              {DIFFICULTIES.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="gearProfile">{t('uploadMountain.fields.gearProfile')}</label>
            <select
              id="gearProfile"
              name="gearProfile"
              value={formData.gearProfile}
              onChange={handleChange}
              className={styles.input}
              required
            >
              <option value="">{t('uploadMountain.selects.gearProfile')}</option>
              <option value="hiking">{t('gearProfilesDetailed.hiking')}</option>
              <option value="easy-alpinism">{t('gearProfilesDetailed.easy-alpinism')}</option>
              <option value="hard-alpinism">{t('gearProfilesDetailed.hard-alpinism')}</option>
              <option value="himalayan">{t('gearProfilesDetailed.himalayan')}</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>{t('uploadMountain.fields.season')}</label>
            <div className={styles.formRow}>
              <select
                id="seasonStart"
                name="seasonStart"
                value={formData.seasonStart}
                onChange={handleChange}
                className={styles.input}
              >
                <option value="">{t('uploadMountain.selects.seasonStart')}</option>
                {MONTHS.map(m => (
                  <option key={m} value={m}>{t(`common.months.${m}`)}</option>
                ))}
              </select>
              <select
                id="seasonEnd"
                name="seasonEnd"
                value={formData.seasonEnd}
                onChange={handleChange}
                className={styles.input}
              >
                <option value="">{t('uploadMountain.selects.seasonEnd')}</option>
                {MONTHS.map(m => (
                  <option key={m} value={m}>{t(`common.months.${m}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">{t('uploadMountain.fields.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: 'description',
                    value: e.target.value.slice(0, 1500),
                    type: 'text'
                  }
                })
              }
              className={styles.textarea}
              placeholder={t('uploadMountain.fields.descriptionPlaceholder')}
              rows="4"
              maxLength="1500"
            ></textarea>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-color)', marginTop: '0.25rem', opacity: 0.7 }}>
              {formData.description.length}/1500 characters
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="images">{t('uploadMountain.fields.images')}</label>
            <input
              type="file"
              id="images"
              name="images"
              onChange={handleImageChange}
              accept="image/*"
              multiple
              required
              className={styles.fileInput}
              ref={fileInputRef}
            />
            {formData.images.length > 0 && (
              <div className={styles.imagePreview}>
                {formData.images.map((imageFile, index) => (
                  <div key={index} className={styles.imagePreviewItem}>
                    <img
                      src={URL.createObjectURL(imageFile)}
                      alt={t('uploadMountain.imagePreviewAlt', { index: index + 1 })}
                      className={styles.previewImage}
                    />
                    <span>{imageFile.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={loading}
          >
            {loading ? t('uploadMountain.buttons.submitting') : t('uploadMountain.buttons.submit')}
          </button>
        </form>
      )}
    </div>
    </>
  );
}

export default UploadPage;
